import React, {useCallback, useContext, useEffect, useRef, useState} from "react"
import RefreshTimer from "../../util/RefreshTimer";
import {AslDeviceOverviewContext} from "./context/AslDeviceOverviewContext";
import Row from "@meridian/components/row";
import {
    assetTypeToConfigTypeDict,
    getAssetRefreshInterval,
    getCurrentDate,
    getStatusRefreshInterval, sortComparator
} from "../../util/Util";
import {FormattedMessage, injectIntl} from "react-intl";
import Button from "@amzn/meridian/button";
import Menu, {MenuItem} from "@amzn/meridian/menu";
import AddAslSingleDeviceModal from "./modal/AddAslSingleDeviceModal";
import Icon from "@amzn/meridian/icon";
import Column from "@amzn/meridian/column";
import Table, {TableCell, TableRow} from "@amzn/meridian/table";
import {ACTIVE, ASCENDING, BLUE_COLOR, DEFAULT_VENDOR_ATTRIBUTES, ORANGE_COLOR, THREE_DOTS} from "../../util/Constants";
import Text from "@amzn/meridian/text";
import Box from "@amzn/meridian/box";
import Loader from "../../loader/Loader";
import Tooltip from "@amzn/meridian/tooltip";
import chevronDownLargeTokens from "@amzn/meridian-tokens/base/icon/chevron-down-large";
import Alert from "@amzn/meridian/alert";
import DeleteSingleDeviceModal from "../../commonui/DeleteSingleDeviceModal";
import plusCircleTokens from "@amzn/meridian-tokens/base/icon/plus-circle";
import trashTokens from "@amzn/meridian-tokens/base/icon/trash";
import EditMacAddressModal from "../../commonui/EditMacAddressModal";
import {getLocalizedMessage} from "../../util/Localization";
import DisableMonitoringModal from "../../commonui/DisableMonitoringModal";
import {
    checkDeviceMonitoringEnabled,
    getMonitoringStatusDetails,
    VENDOR_DISPLAY_VALUES,
    SPEC_VERSION_DISPLAY_VALUES
} from "../../util/Validations";
import {PermissionsContext} from "../../permissions/PermissionsContext";
import {ConditionalWrapping, tooltipWrapper} from "../../util/ConditionalWrapping";

const AslDeviceOverview = ({intl}) => {

    const {viewModel, dispatch} = useContext(AslDeviceOverviewContext);
    const {isAuthorized} = useContext(PermissionsContext);
    const [openActionsMenu, setOpenActionsMenu] = useState({});
    const [openAddModal, setOpenAddModal] = useState(false)
    const [openEditMacModal, setOpenEditMaceModal] = useState(false)
    const [selected, setSelected] = useState("")
    const [openDeleteModal, setOpenDeleteModal] = useState(false)
    const actionButtonRefs = useRef([]);
    const tableEndRef = useRef(null)
    const [openDisableMonitoringModal, setOpenDisableMonitoringModal] = useState(false)
    const [mac, setMac] = useState("");

    const onClickActionsButton = useCallback((index) => {
        let _open = {...openActionsMenu};
        _open[index] = true;
        setOpenActionsMenu(_open);
    }, []);
    const onCloseActions = useCallback((index) => {
        let _open = {...openActionsMenu};
        _open[index] = false;
        setOpenActionsMenu(_open)
    }, []);

    useEffect(() => {
        if (!openAddModal && !openDeleteModal) {
            getData()
            const interval = setInterval(() => {
                if (!document.hidden) {
                    getData();
                }
            }, getAssetRefreshInterval());
            const statusInterval = setInterval(() => {
                if (!document.hidden) {
                    getStatusData();
                }
            }, getStatusRefreshInterval());
            return () => {
                clearInterval(interval);
                clearInterval(statusInterval);
            }
        }
    }, [openAddModal, openDeleteModal]);
    useEffect(() => {
        actionButtonRefs.current = actionButtonRefs.current.slice(0, viewModel.aslDeviceInformation.length);
    }, [viewModel.aslDeviceInformation]);

    const onSort = (sortDetails) => {
        dispatch({
            type: 'SORT_DATA',
            data: {column: sortDetails.sortColumn, direction: sortDetails.sortDirection}
        })
    }
    const sortedDeviceList = Object.values(viewModel.aslDeviceInformation);
    sortedDeviceList && sortedDeviceList.sort((a, b) => {
        const {column, direction} = viewModel.sortPref;
        let compareValue;
        if (Object.keys(DEFAULT_VENDOR_ATTRIBUTES).includes(column)) {
            if(column === "vendor"){
                compareValue = sortComparator(a.vendorAttributes[column]+a.vendorAttributes["spec_version"], b.vendorAttributes[column]+b.vendorAttributes["spec_version"]);
            } else{
                compareValue = sortComparator(a.vendorAttributes[column], b.vendorAttributes[column]);
            }
        } else {
            compareValue = sortComparator(a[column], b[column])
        }
        if (direction === ASCENDING) {
            return compareValue;
        } else {
            return -compareValue;
        }
    })

    const updateData = (stateValues, mac) => {
        dispatch({type: 'UPDATE_MONITORING_DATA', data: {dispatcher: dispatch, stateValues: stateValues, mac: mac}})
    }
    const onClickDisableMonitoring = (mac) => {
        setMac(mac)
        setOpenDisableMonitoringModal(true)
    }
    const onClickEnableMonitoring = (startTime, reason, mac) => {
        const stateValues = {
            "startTime": startTime,
            "endTime": getCurrentDate().toString(),
            "reason": reason
        }
        updateData(stateValues, mac);
        getData();
    }
    const renderDeviceMonitoring = (monitoringStatusColor, monitoringStatusId, monitoringStatusDefaultMessage) => {
        return (<b style={{color: monitoringStatusColor}}>
            <FormattedMessage id={monitoringStatusId} defaultMessage={monitoringStatusDefaultMessage}/>
        </b>)
    }
    const getData = () => {
        setOpenAddModal(false)
        setOpenDeleteModal(false)
        setOpenDisableMonitoringModal(false)
        dispatch({type: 'LOAD_VIEW', data: {dispatcher: dispatch}});
    }
    const getStatusData = () => {
        dispatch({type: 'LOAD_STATUS', data: {dispatcher: dispatch}});
    }
    const scrollToBottom = () => {
        tableEndRef.current.scrollIntoView({behavior: "smooth"})
    }
    const onClickAddEditDevice = () => {
        setSelected("");
        setOpenAddModal(true);
    }
    const onClickEditDevice = (mac) => {
        setSelected(mac)
        setOpenAddModal(true)
    }
    const onClickRemove = (mac) => {
        setSelected(mac)
        setOpenDeleteModal(true)
    }
    const onClickChangeMac = (mac) => {
        setSelected(mac);
        setOpenEditMaceModal(true);
    }
    const getAslStateValues = (mac) => {
        if(!mac) return {};
        const device = viewModel.aslDeviceInformation[mac];
        if(!device) return {};
        const deviceMonitoring = {
            startTime: device.startTime,
            endTime: device.endTime,
            reason: device.reason
        }
        let stateValues;
        if(device.deviceType === 'slam') {
            stateValues = {
                mac: mac,
                ip: device.ip,
                name: device.name,
                deviceType: device.deviceType,
                port: device.port,
                vendorAttributes: device.vendorAttributes,
                deviceConfig: {
                    responsePort: device.responsePort,
                    inductLocation: device.inductLocation,
                    userId: device.userId
                },
                monitoring: JSON.stringify(deviceMonitoring)
            }
        } else {
            stateValues = {
                mac: mac,
                ip: device.ip,
                name: device.name,
                deviceType: device.deviceType,
                port: device.port,
                deviceConfig: {
                    type: assetTypeToConfigTypeDict.typeASL,
                    id: device.id
                },
                vendorAttributes: device.vendorAttributes,
                monitoring: JSON.stringify(deviceMonitoring)
            }
        }
        // Feeds the change-mac modal. Configuration-tab attributes are not taken from page data here:
        // updateAslDeviceMac copies them off the old asset in NARC onto the new mac.
        stateValues.deviceConfig = JSON.stringify(stateValues.deviceConfig)
        return stateValues;
    }

    if (viewModel.loadingOverview) {
        return (<Loader/>)
    } else {
        return (
            <Row widths="100%"
                 alignmentVertical="top"
                 spacingInset="none none large none">
                <Column>
                    <Row width="100%" widths={["100", "fill", "100"]}>
                        <RefreshTimer deps={[viewModel]}/>
                        <Box></Box>
                        <Tooltip position="top" title={intl.formatMessage({
                            id: 'tooltip.scrolltobottom',
                            defaultMessage: 'Scroll to bottom'
                        })}>
                            <Button onClick={scrollToBottom}
                                    type="secondary"
                                    size="small">
                                <Icon tokens={chevronDownLargeTokens}/>
                            </Button>
                        </Tooltip>
                    </Row>
                    {sortedDeviceList.length === 0 ?
                        <Alert type="informational" size="small">
                            <FormattedMessage id="alert.device"
                                              defaultMessage="No device present for this station."/>
                        </Alert> :
                        <Table
                            headerRows={1}
                            spacing="small"
                            sortColumn={viewModel.sortPref.column}
                            sortDirection={viewModel.sortPref.direction}
                            onSort={onSort}
                            showDividers={true}
                            showStripes={true}>
                            <TableRow
                                highlightOnHover={false}>
                                <TableCell sortColumn="name">
                                    <FormattedMessage id="device.name" defaultMessage="Name"/>
                                </TableCell>
                                <TableCell sortColumn="mac">
                                    <FormattedMessage id="device.mac_ip" defaultMessage="MAC/IP"/>
                                </TableCell>
                                <Tooltip position={"top"} title={getLocalizedMessage({intl, stringId: 'tooltip.connectionstatus'})}>
                                    <TableCell sortColumn="connection">
                                        <FormattedMessage id="device.connection" defaultMessage="Connection Status"/>
                                    </TableCell>
                                </Tooltip>
                                <TableCell>
                                    <FormattedMessage id="device.monitoring" defaultMessage="Monitoring Status"/>
                                </TableCell>
                                <TableCell sortColumn="vendor" >
                                    {getLocalizedMessage({intl, stringId: 'device.vendor'})}
                                </TableCell>
                                <TableCell>
                                    <FormattedMessage id="device.action" defaultMessage="Action"/>
                                </TableCell>
                            </TableRow>
                            {sortedDeviceList.map((device, index) => {
                                const vendorAttributes = device.vendorAttributes;
                                const monitoring = checkDeviceMonitoringEnabled(Number(device.startTime), Number(device.endTime));
                                const {monitoringStatusColor, monitoringStatusId, monitoringStatusDefaultMessage} = getMonitoringStatusDetails(monitoring);
                                return (<TableRow key={index}
                                          highlightOnHover={true}>
                                    <TableCell>
                                        <b>{device.name}</b>
                                        <br></br>
                                        {device.deviceType}
                                    </TableCell>
                                    <TableCell>
                                        <Column>
                                            <b>{device.mac}</b>
                                            {device.ip}:{device.port}
                                        </Column>
                                    </TableCell>
                                    <TableCell>
                                        {device.assetStatus ?
                                            <p style={{color: device.assetStatus.toUpperCase() === ACTIVE ? BLUE_COLOR : ORANGE_COLOR}}>
                                                <b>{device.assetStatus}</b>
                                            </p> :
                                            <FormattedMessage id="loading.text" defaultMessage="Loading..."/>}
                                    </TableCell>
                                    <TableCell>
                                        {renderDeviceMonitoring(monitoringStatusColor, monitoringStatusId, monitoringStatusDefaultMessage)}
                                    </TableCell>
                                     <TableCell>
                                         <b>{VENDOR_DISPLAY_VALUES[vendorAttributes.vendor]} {SPEC_VERSION_DISPLAY_VALUES[vendorAttributes.spec_version]}</b>
                                    </TableCell>
                                    <TableCell>
                                        <Text type="b300">
                                            <ConditionalWrapping
                                                condition={!isAuthorized}
                                                wrapper={tooltipWrapper}
                                                args={{
                                                    title: getLocalizedMessage({intl, stringId: "tooltip.isAuthorized"}),
                                                    position: "left"
                                                }}
                                                child={<Button ref={el => actionButtonRefs.current[index] = el}
                                                               type="icon"
                                                               size="medium"
                                                               onClick={() => onClickActionsButton(index)}
                                                               aria-controls="my-menu"
                                                               aria-haspopup="menu"
                                                               aria-expanded="false"
                                                               disabled={!isAuthorized}
                                                >
                                                    {THREE_DOTS}
                                                </Button>}
                                            />
                                            <Menu anchorNode={actionButtonRefs.current[index]}
                                                  open={openActionsMenu[index] ? openActionsMenu[index] : false}
                                                  position="left"
                                                  onClose={() => onCloseActions(index)}>
                                                <MenuItem onClick={() => onClickEditDevice(device.mac)}>
                                                    <FormattedMessage id="actions.addDevice"
                                                                      defaultMessage="Edit"/>
                                                </MenuItem>
                                                <MenuItem onClick={() => onClickRemove(device.mac)}>
                                                    <FormattedMessage id="actions.deleteDevice"
                                                                      defaultMessage="Delete"/>
                                                </MenuItem>
                                                <MenuItem onClick={() => onClickChangeMac(device.mac)}>
                                                    <FormattedMessage id="actions.changeMac"
                                                                      defaultMessage="Change MAC address"/>
                                                </MenuItem>
                                                {(monitoring.isMonitoringEnabled) ?
                                                    <MenuItem onClick={() => onClickDisableMonitoring(device.mac)}>
                                                        <FormattedMessage id="actions.monitoring_disable"
                                                                          defaultMessage="Disable Monitoring"/>
                                                    </MenuItem> : <MenuItem
                                                        onClick={() => onClickEnableMonitoring(device.startTime, device.reason, device.mac)}>
                                                        <FormattedMessage id="actions.monitoring_enable"
                                                                          defaultMessage="Enable Monitoring"/>
                                                    </MenuItem>}
                                            </Menu>
                                        </Text>
                                    </TableCell>
                                </TableRow>
                            )})}
                        </Table>}
                    <Row width="100%" widths={["100", "fill", "100"]}>
                        <ConditionalWrapping
                            condition={!isAuthorized}
                            wrapper={tooltipWrapper}
                            args={{
                                title: getLocalizedMessage({intl, stringId: "tooltip.isAuthorized"}),
                                position: "end"
                            }}
                            child={<Button onClick={onClickAddEditDevice} type="primary" size="small"
                                           disabled={!isAuthorized}
                            >
                                <Icon tokens={plusCircleTokens}/>
                                <FormattedMessage id="device.add_device_button_modal" defaultMessage="Add Device Configuration"/>
                            </Button>}
                        />
                        <Box></Box>
                        <ConditionalWrapping
                            condition={!isAuthorized}
                            wrapper={tooltipWrapper}
                            args={{
                                title: getLocalizedMessage({intl, stringId: "tooltip.isAuthorized"}),
                                position: "end"
                            }}
                            child={<Button onClick={() => setOpenDeleteModal(true)} type="tertiary" size="small"
                                           disabled={!isAuthorized}
                            >
                                <Icon tokens={trashTokens}/>
                                <FormattedMessage id="button.delete" defaultMessage="Delete device"/>
                            </Button>}
                        />
                    </Row>
                    <AddAslSingleDeviceModal open={openAddModal} setOpen={setOpenAddModal}
                                             selected={selected} setSelected={setSelected}/>
                    <DeleteSingleDeviceModal open={openDeleteModal} setOpen={setOpenDeleteModal}
                                             selected={selected} setSelected={setSelected}
                                             macAddressArray={viewModel.macAddressArray} dispatch={dispatch}/>
                    <EditMacAddressModal open={openEditMacModal} setOpen={setOpenEditMaceModal}
                                         selectedMac={selected} setSelectedMac={setSelected} dispatch={dispatch}
                                         stateValues={getAslStateValues(selected)}/>
                    {openDisableMonitoringModal && <DisableMonitoringModal mac={mac} state={openDisableMonitoringModal}
                                                                           onStateChange={setOpenDisableMonitoringModal}
                                                                           context={AslDeviceOverviewContext}
                                                                           reducer={'UPDATE_MONITORING_DATA'}/>}
                    <div ref={tableEndRef}/>
                </Column>
            </Row>
        );
    }
}

export default injectIntl(AslDeviceOverview)



// WEBPACK FOOTER //
// ./src/components/devicemanagement/asldevice/AslDeviceOverview.js